import { useEffect, useState } from "react";
import { createSubmission } from "../api/submissions";
import { useTranslation } from "../context/TranslationContext";
import useSocialAccounts from "../hooks/useSocialAccounts";
import AppIcon from "./ds/AppIcon";
import Badge from "./ds/Badge";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import Modal from "./ds/Modal";
import TextField from "./ds/TextField";
import ConnectAccountModal from "./campaign/ConnectAccountModal";
import InstagramVerificationModal from "./InstagramVerificationModal";
import {
  APP_FILLED,
  APP_TINTED,
  PlatformLogo,
  acceptedPlatforms,
  handleFromLink,
  platformFromLink,
  platformRate,
  shortLink,
  shortRate,
} from "./campaign/shared";

const TILE_COLUMNS = { 3: "min-[600px]:grid-cols-3", 4: "min-[600px]:grid-cols-4" };

const StepLabel = ({ number, children }) => (
  <div className="flex items-center gap-[10px]">
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-app-creator-rewards-deep text-caption-1-semibold text-app-creator-rewards-soft">
      {number}
    </span>
    <p className="text-subheadline-semibold text-label-primary">{children}</p>
  </div>
);

/**
 * Figma V3: pick where the video went, paste its link, send. A platform that isn't
 * connected yet offers to connect it right here, and the link is checked against
 * the chosen platform before anything is sent.
 */
const CampaignSubmissionModal = ({ isOpen, setIsOpen, campaign, isDark, setToast, onSubmitted, onViewSubmissions }) => {
  const { t } = useTranslation();
  const { linkOf, stateOf, saveLink, saving, refresh } = useSocialAccounts(setToast);
  const [selected, setSelected] = useState(null);
  const [link, setLink] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const platforms = acceptedPlatforms(campaign);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(platforms.length === 1 ? platforms[0] : null);
    setLink("");
    setSent(null);
    // Only when it opens: `platforms` is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const close = () => {
    if (!sending) setIsOpen(false);
  };

  const accountState = selected ? stateOf(selected.id) : null;
  const ready = accountState === "ready";
  const found = platformFromLink(link);
  const mismatch = Boolean(selected && found && found.id !== selected.id);
  const canSend = ready && link.trim() && !mismatch;

  const connect = async (value) => {
    const platform = connecting;
    if (!(await saveLink(platform, value))) return;
    setConnecting(null);
    if (platform.id === "instagram") setVerifying(true);
  };

  const send = async () => {
    if (!canSend) return;
    if (campaign.status !== "active" && campaign.status !== "pending") {
      setToast?.(t("campaigns.submissionsClosed"));
      return;
    }
    setSending(true);
    try {
      const response = await createSubmission(campaign._id, selected.id, link.trim());
      if (!response.success) throw new Error(response.message);
      setSent({ link: link.trim(), verified: response.data?.isAuthorVerified });
      onSubmitted?.(response.data);
    } catch (error) {
      setToast?.(error.response?.data?.message || error.message || t("campaigns.failedToSubmitCampaign"));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const thumb = campaign.campaignLogo ? (
    <img src={campaign.campaignLogo} alt="" className="size-[52px] shrink-0 rounded-14 object-cover" />
  ) : (
    <AppIcon app="creator-rewards" size={52} />
  );

  return (
    <>
      {sent ? (
        <Modal open onClose={close} title={t("campaignFlow.sentTitle")} width="min-[600px]:w-[520px]" isDark={isDark} bare>
          <div className="flex flex-col gap-space-24">
            <span className="flex size-16 items-center justify-center rounded-full bg-app-creator-rewards-base text-app-creator-rewards-on">
              <Icon name="check" size={32} />
            </span>
            <div className="flex flex-col gap-space-8">
              <h2 className="text-title-2 text-label-primary">{t("campaignFlow.sentTitle")}</h2>
              <p className="text-body text-label-secondary">
                {t(sent.verified ? "campaigns.submissionCreatedVerified" : "campaigns.submissionCreatedPending")}
              </p>
            </div>
            <div className="flex items-center gap-space-12 rounded-[22px] bg-bg-inset p-space-16">
              <PlatformLogo platform={selected} size={24} />
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="truncate text-subheadline-semibold text-label-primary">{shortLink(sent.link)}</p>
                <p className="truncate text-caption-1 text-label-tertiary">
                  {campaign.campaignName} · {t("campaignFlow.justNow")}
                </p>
              </div>
              <Badge tone="warning">{t("campaignFlow.status_pending")}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-space-8">
              <Button
                variant="gray"
                fullWidth
                onClick={() => {
                  close();
                  onViewSubmissions?.();
                }}
              >
                {t("campaignFlow.mySubmissions")}
              </Button>
              <Button fullWidth className={APP_FILLED} onClick={close}>
                {t("campaignFlow.done")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : (
        <Modal
          open
          onClose={close}
          title={t("campaignFlow.submitTitle")}
          description={campaign.campaignName}
          closeLabel={t("common.close")}
          media={thumb}
          width="min-[600px]:w-[520px]"
          isDark={isDark}
        >
          <div className="flex flex-col gap-[14px]">
            <StepLabel number={1}>{t("campaignFlow.whereDidYouPost")}</StepLabel>
            <div className={`grid grid-cols-2 gap-space-8 ${TILE_COLUMNS[platforms.length] || ""}`}>
              {platforms.map((platform) => {
                const chosen = selected?.id === platform.id;
                const rate = platformRate(campaign, platform);
                return (
                  <button
                    key={platform.id}
                    type="button"
                    aria-pressed={chosen}
                    disabled={sending}
                    onClick={() => setSelected(platform)}
                    className={`flex min-w-0 flex-col items-start gap-space-4 rounded-[18px] p-space-16 text-left outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
                      chosen
                        ? "bg-app-creator-rewards-deep shadow-[inset_0_0_0_2px_var(--app-creator-rewards-base)]"
                        : "bg-bg-inset"
                    }`}
                  >
                    <PlatformLogo platform={platform} size={24} className="mb-space-8" />
                    <span className="w-full truncate text-subheadline-semibold text-label-primary">{platform.name}</span>
                    {rate !== null && (
                      <span
                        className={`whitespace-nowrap rounded-[8px] px-space-8 py-[3px] text-caption-1-semibold ${
                          chosen ? "bg-app-creator-rewards-deep text-app-creator-rewards-soft" : "bg-fill-quaternary text-label-secondary"
                        }`}
                      >
                        {shortRate(rate, campaign.viewsPerRate)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-[14px]">
            <StepLabel number={2}>{t("campaignFlow.pasteLink")}</StepLabel>
            {selected && !ready ? (
              <div className="flex flex-col gap-space-12 rounded-[22px] bg-bg-inset p-space-16">
                <div className="flex items-start gap-space-12">
                  <PlatformLogo platform={selected} size={24} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="text-subheadline-semibold text-label-primary">
                      {t(accountState === "missing" ? "campaignFlow.connectFirst" : "campaignFlow.verifyFirst", {
                        platform: selected.name,
                      })}
                    </p>
                    <p className="text-footnote text-label-secondary">{t("campaignFlow.connectWhy")}</p>
                  </div>
                </div>
                <Button
                  variant="tinted"
                  size="m"
                  fullWidth
                  className={APP_TINTED}
                  icon={<Icon name="link" size={20} />}
                  onClick={() => (accountState === "missing" ? setConnecting(selected) : setVerifying(true))}
                >
                  {t(accountState === "missing" ? "campaignFlow.connectPlatform" : "campaignFlow.verifyPlatform", {
                    platform: selected.name,
                  })}
                </Button>
              </div>
            ) : (
              <TextField
                label={t("campaigns.videoLink")}
                type="url"
                value={link}
                onChange={(event) => setLink(event.target.value)}
                placeholder={t(`createCampaignWizard.videoPlaceholder${selected ? selected.id[0].toUpperCase() + selected.id.slice(1) : "Default"}`)}
                icon={<Icon name="link" size={20} />}
                disabled={sending || !selected}
                success={Boolean(selected && found && !mismatch)}
                error={mismatch ? t("campaignFlow.linkMismatch", { found: found.name, selected: selected.name }) : undefined}
                hint={found && !mismatch ? t("campaignFlow.linkFound", { platform: found.name }) : undefined}
              />
            )}
          </div>

          <div className="flex items-center gap-[10px] rounded-14 bg-fill-quaternary px-[14px] py-space-12 text-label-secondary">
            <Icon name="clock" size={16} className="shrink-0" />
            <p className="text-footnote">{t("createCampaignWizard.twentyMinuteRule", { minutes: 20 })}</p>
          </div>

          <div className="flex gap-space-8">
            <Button variant="gray" disabled={sending} onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button fullWidth className={`flex-1 ${canSend && !sending ? APP_FILLED : ""}`} disabled={!canSend} loading={sending} onClick={send}>
              {sending ? t("campaignFlow.sending") : t("common.submit")}
            </Button>
          </div>
        </Modal>
      )}

      <ConnectAccountModal platform={connecting} saving={saving} onSave={connect} onClose={() => setConnecting(null)} />
      <InstagramVerificationModal
        isOpen={verifying}
        setIsOpen={(open) => {
          setVerifying(open);
          if (!open) refresh();
        }}
        setToast={setToast}
        isDark={isDark}
        initialUsername={handleFromLink(linkOf("instagram")).replace(/^@/, "")}
        onVerificationUpdate={(verified) => verified && refresh()}
      />
    </>
  );
};

export default CampaignSubmissionModal;
